import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { type PageLayoutStore } from 'shared/layout'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { InboxTree } from 'entities/Inbox/model/Dnd/InboxTree'
import { type IInboxTreeCombine, TreeRootInboxes } from 'entities/Inbox'
import { GroupInbox } from 'entities/Inbox/model/GroupInbox'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { SmartView } from 'entities/SmartViews/model/SmartView'
import { type CallModalStore } from 'widgets/CallModal'
import { type ContactCreateModalStore } from 'widgets/ContactCreateModal'
import {
  type IAddItemTree,
  type IConversationMenuStoreConfig,
  type IHandleUpdateTeamsFavorite,
} from 'pages/conversations/ui/ConversationMenu/store/types'
import { ConversationsPath } from 'pages/conversations/route/conversationsPath'
import { ConversationRoutes } from 'pages/conversations/route/type'
import { QuickFiltersMenuStore } from 'pages/conversations/ui/ConversationMenu/store/quickFiltersMenuStore'
import { SmartViewsMenuStore } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/MenuSectionSmartViewsTree'
import { FavoritesMenuStore } from 'pages/conversations/ui/ConversationMenu/store/favoritesMenuStore'
import { SimpleMenuStore } from 'pages/conversations/ui/ConversationMenu/store/simpleMenuStore'
import { GroupsApiMenuStore } from 'pages/conversations/ui/ConversationMenu/store/modules/groupsApiMenuStore'
import { SharedApiMenuStore } from 'pages/conversations/ui/ConversationMenu/store/modules/sharedApiMenuStore'

export class ConversationMenuStore {
  private _inboxesTreeMap: Map<string, InboxTree> = new Map()
  private _loading = false

  private _loadingUpdateTeam = false
  private _loadingUser = true
  private _disabledActive = false

  private _disposerInboxSize: IReactionDisposer | null = null
  private _config: IConversationMenuStoreConfig | null = null

  readonly quickFiltersMenuStore: QuickFiltersMenuStore
  readonly smartViewsMenuStore: SmartViewsMenuStore
  readonly favoritesMenuStore: FavoritesMenuStore
  readonly simpleMenuStore: SimpleMenuStore

  readonly groupsApiMenuStore: GroupsApiMenuStore
  readonly sharedApiMenuStore: SharedApiMenuStore

  constructor(
    private _pageLayoutStore: PageLayoutStore,
    private _callModalStore: CallModalStore,
    private _contactCreateModalStore: ContactCreateModalStore,
    private _entities: IEntitiesStore
  ) {
    this.sharedApiMenuStore = new SharedApiMenuStore(this, this._entities)
    this.groupsApiMenuStore = new GroupsApiMenuStore(this, this._entities)

    this.quickFiltersMenuStore = new QuickFiltersMenuStore(this, this._entities)
    this.smartViewsMenuStore = new SmartViewsMenuStore(this, this._entities)
    this.favoritesMenuStore = new FavoritesMenuStore(this, this._entities)
    this.simpleMenuStore = new SimpleMenuStore(this, this._entities)

    makeAutoObservable(this)
  }

  reactionInboxSize = () => {
    this._disposerInboxSize?.()
    this._disposerInboxSize = reaction(
      () => this._entities.inboxesStore.personalInboxes.length,
      () => {
        this._disposeTree()
        this.initInboxesTree()
      }
    )
  }

  setConfig = (config?: IConversationMenuStoreConfig) => {
    this._config = {
      ...this._config,
      ...config,
    }
  }

  setDisabledActive = (value: boolean) => {
    this._disabledActive = value
  }

  private _disposeTree = () => {
    this._inboxesTreeMap.clear()
    this._disabledActive = false
  }

  dispose = () => {
    this.smartViewsMenuStore.dispose()
    this._disposeTree()
    this._disposerInboxSize?.()
  }

  setLoadingUser = (value: boolean) => {
    this._loadingUser = value
  }

  init = async () => {
    runInAction(() => {
      this._loading = true
    })

    this.reactionInboxSize()

    await this._entities.featureFlagsStore.fetchFeatureFlags()
    await Promise.all([this.initInboxes(), this.quickFiltersMenuStore.initQuickFilters()])

    runInAction(() => {
      this._loading = false
    })
  }

  initInboxes = async () => {
    try {
      await Promise.all([
        this._entities.inboxesStore.fetchInboxes(),
        this._entities.smartViewsStore.fetchSmartViews(),
      ])

      this._disposeTree()
      this.initInboxesTree()
    } catch (e) {
      logger.error(e)
    }
  }

  initInboxesTree = () => {
    const inboxes = this._entities.inboxesStore.personalInboxes
    const smartViews = this._entities.smartViewsStore.itemsList

    this._inboxesTreeMap = this.addItemsTree([...inboxes, ...smartViews])
  }

  addItemsTree = (inboxes: IInboxTreeCombine[]) => {
    inboxes.forEach((inbox) => {
      this.addItemTree({
        inbox,
      })
    })

    return this._inboxesTreeMap
  }

  addItemTree = ({ inbox, groupInbox }: IAddItemTree) => {
    if (inbox instanceof GroupInbox) {
      const hasAccessibleChildren = inbox.teamsIds.some(
        (teamId) => !!this._entities.inboxesStore.getItem(teamId)
      )

      if (!hasAccessibleChildren) return this._inboxesTreeMap
    }

    this._inboxesTreeMap.set(inbox.idTree, new InboxTree(inbox))

    if (groupInbox instanceof GroupInbox) {
      this.groupsApiMenuStore.setCreateFromGroupInbox(groupInbox)
    }

    if (inbox instanceof GroupInbox) {
      inbox.teamsIds.forEach((teamId) => {
        const inboxChild = this._entities.inboxesStore.getItem(teamId)

        if (!inboxChild) return

        const id = `${inbox.root_tree}_${inbox.id}_${inboxChild.id}`
        inboxChild.root_tree = inbox.root_tree

        this._inboxesTreeMap.set(id, new InboxTree(inboxChild, inbox))
      })
    }

    return this._inboxesTreeMap
  }

  updateItemTree = (id: string, props: { id?: string; parent?: string } = {}) => {
    const inboxTree = this._inboxesTreeMap.get(id)

    if (inboxTree) {
      this.deleteItemTree(inboxTree.id, 'child')

      for (const key in props) {
        if (key === 'id' && props.id) inboxTree.id = props.id
        if (key === 'parent' && props.parent) {
          inboxTree.parent = props.parent
          inboxTree.data.root_tree = props.parent
        }
      }

      this._inboxesTreeMap.set(inboxTree.id, inboxTree)

      if (!inboxTree.data.isGroupInbox) {
        this.updateItemTreeChild(inboxTree.id)
      }

      return inboxTree
    }
  }

  updateItemTreeChild = (id: string) => {
    const inboxTree = this._inboxesTreeMap.get(id)
    const inbox = inboxTree?.data

    if (inboxTree) {
      this._inboxesTreeMap.forEach((item) => {
        const idSplit = item.id.toString().split('_')
        const hasChild = idSplit.length === 3

        if (hasChild) {
          const currentRootTreeParent = idSplit[0]
          const currentIdTreeParent = +idSplit[1]
          const currentIdTreeChild = +idSplit[2]

          if (currentIdTreeChild === inbox?.id) {
            const groupInbox = this._entities.inboxesStore.getItem(currentIdTreeParent)

            if (!groupInbox) return

            const oldIdTree = `${currentRootTreeParent}_${currentIdTreeParent}_${currentIdTreeChild}`
            const newIdTree = `${groupInbox.root_tree}_${currentIdTreeParent}_${currentIdTreeChild}`
            const newRootTree = `${groupInbox.root_tree}_${currentIdTreeParent}`

            this.deleteItemTree(oldIdTree, 'child')

            if (groupInbox?.type === 'inbox_group') {
              if (groupInbox?.teamsIds.map((teamId) => teamId).includes(inbox.id)) {
                item.id = newIdTree
                item.parent = newRootTree
              }
            }

            setTimeout(() => {
              this._inboxesTreeMap.set(newIdTree, item)
            }, 10)
          }
        }
      })
    }
  }

  deleteItemTree = (
    id: string | number,
    type: 'all' | 'child' | 'delete' = 'all',
    variant: 'default' | 'smart' = 'default'
  ) => {
    let inboxTree: InboxTree | undefined
    let inbox: IInboxTreeCombine | undefined
    let idTree

    if (typeof id === 'string' && type === 'delete') {
      this._inboxesTreeMap.delete(id)

      return
    }

    if (typeof id === 'string') {
      idTree = id
      inboxTree = this._inboxesTreeMap.get(idTree)
      inbox = inboxTree?.data
    } else {
      if (variant === 'smart') {
        inbox = this._entities.smartViewsStore.getItem(id)
      } else {
        inbox = this._entities.inboxesStore.getItem(id)
      }

      idTree = inbox?.idTree
    }

    if (inbox) {
      if (type === 'all') {
        if (inbox instanceof SmartView) {
          this._entities.smartViewsStore.deleteItem(inbox.id)
        } else {
          this._entities.inboxesStore.deleteItem(inbox.id)
        }

        this._inboxesTreeMap.delete(inbox.idTree)
      }
    }

    if (type === 'child') {
      if (idTree) this._inboxesTreeMap.delete(idTree)
    }
  }

  getItemTree = (id?: string) => {
    if (!id) return

    return this._inboxesTreeMap.get(id)
  }

  updateRootIdTree = (inbox: IInboxTreeCombine, dropTargetId: string) => {
    const idTree = inbox.idTree

    this.updateItemTree(idTree, {
      id: `${dropTargetId}_${inbox.id}`,
      parent: dropTargetId,
    })
  }

  updateFavorite = async ({ inbox, groupInbox, status }: IHandleUpdateTeamsFavorite) => {
    const rootId = status
      ? TreeRootInboxes.FAVORITE
      : inbox.isSmartViewInbox
      ? TreeRootInboxes.SMART_VIEW
      : TreeRootInboxes.SIMPLE

    if (groupInbox?.type === 'inbox_group' && inbox.type === 'inbox') {
      await this.groupsApiMenuStore.onDeleteTeamsGroupsByIdRemoveTeamById(inbox, groupInbox)
    }

    if (inbox.type === 'smart_view-inbox') {
      await this._entities.smartViewsStore.updateFavorite(inbox, status)
    } else {
      await this._entities.inboxesStore.updateFavorite(inbox, status)
    }

    this.updateRootIdTree(inbox, rootId)

    if (inbox.type === 'inbox_group') {
      const requests: Promise<void>[] = []

      inbox.teamsIds.forEach((itemId) => {
        const inbox = this._entities.inboxesStore.getItem(itemId)
        if (!inbox) return
        requests.push(
          this.updateFavorite({
            inbox: inbox,
            status: status,
          })
        )
      })

      await Promise.all(requests)
    }
  }

  resetCurrentConversation = () => {
    uiStore.changeRoute({
      path: `/${ConversationsPath.root}`,
    })
    this._entities.conversationStore.setCurrentItemId(null)
  }

  handleUpdateTeamInbox = async (inboxId: number) => {
    runInAction(() => {
      this._loadingUpdateTeam = true
    })

    await this._entities.inboxesStore.handleUpdateTeamInbox(inboxId, 'fast')

    runInAction(() => {
      this._loadingUpdateTeam = false
    })
  }

  private get _selectInboxesFree() {
    return Array.from(this._inboxesTreeMap.values()).filter(
      (item) => item.data.type !== 'unified-inbox'
    )
  }

  get selectInboxesFreeSort() {
    const sort = {
      unified: 0,
      group: 1,
      shared: 3,
      smart_view: 3,
    }

    return this._selectInboxesFree
      .sort((a, b) => a.data.name.localeCompare(b.data.name))
      .sort((a, b) => {
        const inboxA = a.data
        const inboxB = b.data
        const aValue = sort[inboxA.typeKey]
        const bValue = sort[inboxB.typeKey]

        return aValue - bValue
      })
      .sort((a, b) => {
        const inboxA = a.data.isMuted
        const inboxB = b.data.isMuted

        return Number(inboxA) - Number(inboxB)
      })
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get callModalStore() {
    return this._callModalStore
  }

  get contactCreateModalStore() {
    return this._contactCreateModalStore
  }

  get loading() {
    return this._loading
  }

  get loadingUser() {
    if (!this._entities.inboxesStore.isUnifiedInbox) return false

    return this._loadingUser
  }

  get loadingUpdateTeam() {
    return this._loadingUpdateTeam
  }

  get disabledActive() {
    return this._disabledActive
  }

  get config() {
    return this._config
  }

  get isActiveConversationSearch() {
    return uiStore.pathName.includes(`/${ConversationRoutes.search}`)
  }
}
